import type { ExportDemandGroupsRow } from '@ebalance/proto/bffuser/v1/demand_group_service_pb'

import {
  demandGroupCsvDataTypeName,
  serializeDemandGroupCsvRows,
} from '~/models/demandGroupCsvHeader'
import { commonUploadCsvFileName } from '~/pages/AdminUploads/models/downloadFileName'

/**
 * 需要グループ CSV エクスポートの行変換・CSV 組み立て
 * (uploads/common.md §6 AC-DUC-028/029、列定義は
 * docs/specs/admin/data/uploads/demand-groups.md §1)。
 *
 * 出力列はアップロード CSV と同一の 6 列 (日本語表示は 需要BGコード, 小売事業者コード,
 * グループ名, 供給地点特定番号, 名称, 削除フラグ。英語表示は英語ヘッダー)。削除フラグ列はエクスポート
 * 対象が論理削除されていない行のみ (backend `ListAllForExport` の doc 参照)
 * なので常に空欄を書けば足りる。CSV 組み立て自体は雛形 DL
 * (`~/pages/DemandGroupCsvUpload/models/demandGroupCsvTemplate`) と共有する
 * `~/models/demandGroupCsvHeader` の `serializeDemandGroupCsvRows` に委譲する
 * (逐語複製を避ける)。
 */

/** CSV 1 行分のデータ。 */
export type DemandGroupCsvExportRow = {
  demandBgCode: string
  retailerCode: string
  groupName: string
  supplyPointSpecificNumber: string
  /** 供給地点のメモ (任意)。未設定 = 空セル。 */
  name: string
}

export function toDemandGroupCsvExportRow(
  row: ExportDemandGroupsRow,
): DemandGroupCsvExportRow {
  return {
    demandBgCode: row.demandBgCode,
    retailerCode: row.retailerCode,
    groupName: row.groupName,
    supplyPointSpecificNumber: row.supplyPointSpecificNumber,
    name: row.name ?? '',
  }
}

/** 応答全行を CSV 行へ変換する (順序は backend が決めた並びを保つ)。 */
export function toDemandGroupCsvExportRows(
  rows: readonly ExportDemandGroupsRow[],
): DemandGroupCsvExportRow[] {
  return rows.map(toDemandGroupCsvExportRow)
}

/**
 * エクスポート行から CSV 本文を組み立てる (F1 純関数)。削除フラグ列は
 * エクスポート対象が論理削除されていない行のみ (上記 doc 参照) なので、
 * 共有 serializer へは常に空欄で渡す。区切り・改行・rows 空時の扱いは
 * `serializeDemandGroupCsvRows` (単一 SoT) に従う。
 */
export function buildDemandGroupCsvExport(
  rows: readonly DemandGroupCsvExportRow[],
  isEnglish: boolean,
): string {
  return serializeDemandGroupCsvRows(
    rows.map((row) => ({ ...row, deleteFlag: '' })),
    isEnglish,
  )
}

/**
 * 入稿済みデータのファイル名 (`{データ種別名}_{YYYYMMDD}.csv`、common.md §6。
 * 英語表示は英語データ種別名 `demand-groups`)。
 */
export function demandGroupCsvExportFileName(
  isEnglish: boolean,
  today?: Date,
): string {
  return commonUploadCsvFileName(demandGroupCsvDataTypeName(isEnglish), today)
}
